import React, { useEffect, useMemo, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { hasPositionBarrier } from "../../Helpers";

const DOCKING_SIDE_PORT = "port";
const DOCKING_SIDE_STARBOARD = "starboard";

type MobileVesselPositionGridProps = {
  vessel?: any;
  containers: any[];
  direction?: "loading" | "unloading";
  onContainerClick?: (container: any) => void;
  compact?: boolean;
  focusedContainer?: any;
  dockingSide?: string;
  onPositionClick?: (position: [number, number, number]) => void;
  getCellPresentation?: (context: {
    layer: number;
    column: number;
    slot: number;
    container?: any;
    isGhost: boolean;
    isFiller: boolean;
  }) => {
    className?: string;
    style?: React.CSSProperties;
    content?: React.ReactNode;
    disabled?: boolean;
    ariaLabel?: string;
  } | undefined;
};

const getPosition = (container: any): number[] =>
  Array.isArray(container?.position)
    ? container.position.slice(0, 3).map((value: any) => Number(value))
    : [];

const MobileVesselPositionGrid: React.FC<MobileVesselPositionGridProps> = ({
  vessel,
  containers,
  direction = "loading",
  onContainerClick,
  compact = false,
  focusedContainer,
  dockingSide = DOCKING_SIDE_PORT,
  onPositionClick,
  getCellPresentation,
}) => {
  const { t } = useTranslation();
  const [selectedLayer, setSelectedLayer] = useState(0);

  const positionedContainers = useMemo(
    () => (containers || []).filter((container) => getPosition(container).length === 3),
    [containers]
  );

  const layerDefinitions = useMemo(() => {
    if (Array.isArray(vessel?.positions) && vessel.positions.length) {
      return vessel.positions
        .map((columns: any, index: number) => ({ layer: index + 1, columns }))
        .filter((definition: any) => definition.layer !== 5);
    }

    const layers = new Map<number, number[]>();
    positionedContainers.forEach((container) => {
      const [layer, column, slot] = getPosition(container);
      const columns = layers.get(layer) || [];
      columns[column - 1] = Math.max(columns[column - 1] || 0, slot);
      layers.set(layer, columns);
    });

    return Array.from(layers.entries())
      .sort(([left], [right]) => left - right)
      .map(([layer, columns]) => ({
        layer,
        columns: Array.from(
          { length: columns.length },
          (_, columnIndex) => columns[columnIndex] || 1
        ),
      }));
  }, [positionedContainers, vessel]);

  const safeLayerIndex = Math.min(selectedLayer, Math.max(layerDefinitions.length - 1, 0));
  const activeDefinition = layerDefinitions[safeLayerIndex];

  useEffect(() => {
    if (!focusedContainer || !layerDefinitions.length) return;
    const [focusedLayer] = getPosition(focusedContainer);
    const focusedLayerIndex = layerDefinitions.findIndex(
      (definition) => definition.layer === focusedLayer
    );
    if (focusedLayerIndex >= 0) setSelectedLayer(focusedLayerIndex);
  }, [focusedContainer, layerDefinitions]);

  const visibleContainers = focusedContainer ? [focusedContainer] : positionedContainers;

  const findContainer = (layer: number, column: number, slot: number) =>
    visibleContainers.find((container) => {
      const position = getPosition(container);
      return position[0] === layer && position[1] === column && position[2] === slot;
    });

  if (!activeDefinition) {
    return <Typography className="mobile-empty-message">{t("noVesselGridPosition", "No vessel grid positions configured")}</Typography>;
  }

  return (
    <Box className="mobile-position-planner">
      {compact ? (
        <Box className="mobile-position-compact-heading">
          <strong>{t("positionOnBoard", "Position on board")}</strong>
          <span>
            {focusedContainer
              ? `${t("layer", "Layer")} ${getPosition(focusedContainer)[0]} · ${t("column", "Column")} ${getPosition(focusedContainer)[1]} · ${t("position", "Position")} ${getPosition(focusedContainer)[2]}`
              : t("notPositioned", "Not positioned")}
          </span>
        </Box>
      ) : (
        <Box className="mobile-position-controls">
          <Tabs
            value={safeLayerIndex}
            onChange={(_, value) => setSelectedLayer(value)}
            variant="scrollable"
            scrollButtons={false}
            className="mobile-layer-tabs"
          >
            {layerDefinitions.map((definition) => (
              <Tab key={definition.layer} label={`L${definition.layer}`} />
            ))}
          </Tabs>
        </Box>
      )}

      <Box className={`mobile-position-grid-scroll${compact ? " is-compact" : ""}`}>
        <Box className={`mobile-position-ship${dockingSide === DOCKING_SIDE_STARBOARD ? " is-starboard" : ""}`}>
          <span className="mobile-position-bow"><span className="mobile-position-layer-label">{activeDefinition.layer}</span></span>
          <Box className="mobile-position-columns">
            {(() => {
              const maxRenderedGridCount = activeDefinition.columns.reduce((max, gridCount) => {
                const n = Number(gridCount) || 0;
                return Math.max(max, n === 2 ? 4 : n === 3 ? 5 : n);
              }, 0);

              return activeDefinition.columns.map((gridCount, columnIndex) => {
              const normalizedGridCount = Number(gridCount) || 0;
              const columnHasGhostGrid =
                normalizedGridCount === 2 || normalizedGridCount === 3;
              const renderedGridCount =
                normalizedGridCount === 2
                  ? 4
                  : normalizedGridCount === 3
                    ? 5
                    : normalizedGridCount;

              return (
              <React.Fragment key={`${activeDefinition.layer}-${columnIndex}`}>
              <Box className={`mobile-position-column${columnHasGhostGrid ? " is-ghost" : ""}`}>
                <span className="mobile-position-column-label">{columnIndex + 1}</span>
                {Array.from({ length: maxRenderedGridCount }).map((_, slotIndex) => {
                  const slot = slotIndex + 1;
                  const isFiller = slot > renderedGridCount;
                  const isGhostGrid =
                    !isFiller && (
                      (normalizedGridCount === 2 && (slot === 1 || slot === 4)) ||
                      (normalizedGridCount === 3 && (slot === 1 || slot === 5))
                    );
                  const container = !isFiller ? findContainer(activeDefinition.layer, columnIndex + 1, slot) : undefined;
                  const isDummy = Boolean(container?.dummy);
                  const isPositioned = Boolean(container);
                  const isSpecialGhostGrid =
                    isGhostGrid &&
                    columnIndex + 1 === activeDefinition.columns.length &&
                    normalizedGridCount === 3;
                   let className = [
                    "mobile-position-cell",
                    "container-planner-grid",
                    isPositioned &&
                      !isDummy &&
                      `container-planner-grid-teu-${container?.containerType?.teu ?? 1}`,
                    isDummy && "container-planner-grid--dummy",
                    (isGhostGrid || isFiller) && "container-planner-ghost-grid",
                    isSpecialGhostGrid && "container-planner-ghost-grid--small",
                    isGhostGrid && "is-ghost",
                    container && "is-filled",
                    focusedContainer && container && "is-focused",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  const color =
                    direction === "loading"
                      ? container?.unloadingTerminalCall?.terminal?.color ||
                        container?.unloadingTerminal?.color ||
                        "#5bd37d"
                      : container?.loadingTerminalCall?.terminal?.color ||
                        container?.loadingTerminal?.color ||
                        "#5bd37d";
                  const presentation = getCellPresentation?.({
                    layer: activeDefinition.layer,
                    column: columnIndex + 1,
                    slot,
                    container,
                    isGhost: isGhostGrid,
                    isFiller,
                  });
                  if (presentation?.className) {
                    className += ` ${presentation.className}`;
                  }

                  return (
                    <button
                      type="button"
                      key={slot}
                      className={className}
                      style={{
                        ...(container ? { backgroundColor: color } : {}),
                        ...presentation?.style,
                      }}
                      disabled={Boolean(isGhostGrid || isFiller || presentation?.disabled)}
                      onClick={() => {
                        if (container) onContainerClick?.(container);
                        onPositionClick?.([
                          activeDefinition.layer,
                          columnIndex + 1,
                          slot,
                        ]);
                      }}
                      aria-label={presentation?.ariaLabel || container?.uid || `${t("position", "Position")} ${columnIndex + 1}-${slot}`}
                    >
                      {presentation?.content ?? (container ? <span>{String(container.uid || "").slice(-4)}</span> : null)}
                    </button>
                  );
                })}
              </Box>
              {Array.isArray(vessel?.barriers) &&
              hasPositionBarrier([columnIndex + 1, columnIndex + 2], vessel) ? (
                <Box
                  className="mobile-position-barrier container-planner-barrier"
                  aria-hidden="true"
                />
              ) : null}
              </React.Fragment>
              );
            })})()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileVesselPositionGrid;
